import React, {
  useEffect,
  useState,
  useRef,
  useCallback,
  useImperativeHandle,
} from "react";
import {
  Autocomplete,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Button,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  ListSubheader,
  Link,
  Tooltip,
} from "@mui/material";
import { grey } from "@mui/material/colors";
import DeleteIcon from "@mui/icons-material/Delete";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import StopIcon from "@mui/icons-material/Stop";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import CloseIcon from "@mui/icons-material/Close";
import InfoIcon from "@mui/icons-material/Info";
import LockIcon from "@mui/icons-material/Lock";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DateTimePicker, TimePicker } from "@mui/x-date-pickers";
import { renderTimeViewClock } from "@mui/x-date-pickers/timeViewRenderers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { styled } from "@mui/material/styles";
import loop4Img from "./loop4.png";
import { isSessionValid } from "./utils";
import dayjs from "dayjs";
import updateLocale from "dayjs/plugin/updateLocale";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

dayjs.extend(updateLocale);
dayjs.extend(utc);
dayjs.extend(timezone);

dayjs.updateLocale("en", {
  weekStart: 1, // Set Monday as the first day of the week, for right display in calendars
});

dayjs.tz.setDefault("Europe/Helsinki");

const DEFAULT_HOURS_IN_SECONDS = 3600;
const MIN_HOURS_IN_SECONDS = 60;
const AUTOCOMPLETE_MAX = 7; // max number of autocomplete suggestions
const TYPING_DEBOUNCE_MS = 180; // delay search until user pauses typing
const TRIGRAM_LEN = 3; // query length where we switch to trigram search
const FETCH_ROW_LIMIT = 350; // max rows to fetch at once

const StyledTableHeaderCell = styled(TableCell, {
  shouldForwardProp: (prop) => prop !== "span",
})(({ span }) => {
  const totalColumns = 16;
  const width = (span / totalColumns) * 100;
  return {
    width: `${width}%`,
    padding: 0,
    borderBottom: "none",
  };
});

const StyledIconButton = styled(IconButton)({
  border: "none",
  "&:hover": {
    border: "none",
    backgroundColor: "transparent",
    "& .MuiSvgIcon-root": {
      transform: "scale(1.3)",
    },
  },
  "& .MuiSvgIcon-root": {
    transition: "transform 0.5s ease",
  },
});

const WorkHoursTable = ({ authToken, onLogout, onViewChange }) => {
  const [workHours, setWorkHours] = useState([]);
  const [clients, setClients] = useState([]);
  const [activities, setActivities] = useState([]);
  const [types, setTypes] = useState([]);
  const [editing, setEditing] = useState(null);
  const defaultTime = dayjs().subtract(DEFAULT_HOURS_IN_SECONDS, "seconds");
  const [form, setForm] = useState({
    date: defaultTime,
    hours: DEFAULT_HOURS_IN_SECONDS,
    description: "",
    client: "",
    activity: "",
    type: "",
  });
  const [editForm, setEditForm] = useState({
    date: defaultTime,
    hours: DEFAULT_HOURS_IN_SECONDS,
    description: "",
    client: "",
    activity: "",
    type: "",
  });
  const [formErrors, setFormErrors] = useState({});
  const [isPlaying, setIsPlaying] = useState(false);
  const [runningTimerId, setRunningTimerId] = useState(null);
  const [runningTimerStart, setRunningTimerStart] = useState(null);
  const [loading, setLoading] = useState(true);
  const timerDisplayRef = useRef(null);
  const [suggestions, setSuggestions] = useState([]);
  const [serverTotalCount, setServerTotalCount] = useState(null);
  const [showCountsBox, setShowCountsBox] = useState(false);

  useEffect(() => {
    if (!loading) {
      const raf = requestAnimationFrame(() => setShowCountsBox(true)); // show on next paint
      return () => cancelAnimationFrame(raf);
    }
    setShowCountsBox(false); // hide while (re)loading
  }, [loading]);

  //advanced autocomplete for description
  const [descInput, setDescInput] = useState("");
  const [derivedDescOptions, setDerivedDescOptions] = useState([]);
  const [descQuery, setDescQuery] = useState(""); // debounced value used for lookup
  const [typing, setTyping] = useState(false); // true while user is actively typing
  const [descLoading, setDescLoading] = useState(false);
  const [suggestionsNorm, setSuggestionsNorm] = useState([]); // lowercased + trimmed
  const descWorkerRef = useRef(null);
  const lastQueryIdRef = useRef(0);
  const suggestionsRef = useRef(suggestions);
  useEffect(() => {
    suggestionsRef.current = suggestions;
  }, [suggestions]);
  const [indexReady, setIndexReady] = useState(false);
  const topSuggestions = React.useMemo(
    () => suggestions.slice(0, AUTOCOMPLETE_MAX),
    [suggestions]
  );
  const [descOpen, setDescOpen] = useState(false);
  useEffect(() => {
    // Debounce: update descQuery only after the user pauses typing
    setTyping(true);
    // Invalidate any in-flight worker results for the *previous* query
    lastQueryIdRef.current++;
    // Immediately clear old options so the popup can't flash stale items
    setDerivedDescOptions([]);
    const t = setTimeout(() => {
      setDescQuery(descInput);
      setTyping(false);
    }, TYPING_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [descInput]);

  const [editingFocusField, setEditingFocusField] = useState(null); // "startTime" | "hours" | "endTime" | null

  const activityMap = React.useMemo(() => {
    const m = {};
    for (const a of activities) m[a._id] = a;
    return m;
  }, [activities]);

  const clientMap = React.useMemo(() => {
    const m = {};
    for (const c of clients) m[c._id] = c;
    return m;
  }, [clients]);

  const activitiesByClientId = React.useMemo(() => {
    const m = {};
    for (const a of activities) {
      if (!m[a.clientId]) m[a.clientId] = [];
      m[a.clientId].push(a);
    }
    for (const id in m) m[id].sort((a, b) => a.name.localeCompare(b.name));
    return m;
  }, [activities]);

  const clientActivityMenuItems = React.useMemo(() => {
    const items = [];
    for (const client of clients) {
      items.push(
        <ListSubheader key={`client-${client._id}`}>
          {client.name}
        </ListSubheader>
      );
      const list = activitiesByClientId[client._id] || [];
      for (const activity of list) {
        items.push(
          <MenuItem key={activity._id} value={activity._id}>
            {activity.name}
          </MenuItem>
        );
      }
    }
    return items;
  }, [clients, activitiesByClientId]);

  const getClientActivityDisplay = React.useCallback(
    (workHour) => {
      const activity = activityMap[workHour.activity];
      const client = activity ? clientMap[activity.clientId] : undefined;
      return client ? `${client.name}: ${activity.name}` : activity?.name || "";
    },
    [activityMap, clientMap]
  );

  useEffect(() => {
    const fetchRunningTimerData = async () => {
      try {
        const runningTimerResponse = await fetch(
          `${process.env.REACT_APP_API_URL}/workhours/running`,
          {
            headers: { Authorization: `Bearer ${authToken}` },
          }
        );
        if (runningTimerResponse.ok) {
          const runningTimerData = await runningTimerResponse.json();
          if (runningTimerData.running !== false) {
            setForm({
              ...runningTimerData,
              date: dayjs.unix(runningTimerData.date),
            });
            setRunningTimerId(runningTimerData._id);
            setRunningTimerStart(runningTimerData.date);
            setIsPlaying(true);
          } else {
            setIsPlaying(false);
            setRunningTimerStart(null);
          }
        } else {
          console.error(
            "Failed to fetch running timer:",
            runningTimerResponse.statusText
          );
        }
      } catch (error) {
        console.error("Error fetching running timer:", error);
      }
    };

    const fetchData = async () => {
      try {
        const workHoursResponse = await fetch(
          `${process.env.REACT_APP_API_URL}/workhours?limit=${FETCH_ROW_LIMIT}`,
          {
            headers: { Authorization: `Bearer ${authToken}` },
          }
        );
        const totalHeader = workHoursResponse.headers.get("X-Total-Count");
        const totalCount = totalHeader ? parseInt(totalHeader, 10) : null;
        setServerTotalCount(Number.isFinite(totalCount) ? totalCount : null);
        const workHoursData = await workHoursResponse.json();
        const convertedWorkHours = workHoursData.map((workHour) => ({
          ...workHour,
          date: dayjs.unix(workHour.date),
        }));
        setWorkHours(convertedWorkHours);

        if (convertedWorkHours.length > 0) {
          const seen = new Set(); // lowercased+trimmed
          const uniq = [];
          const uniqNorm = [];
          for (const item of convertedWorkHours) {
            const orig = (item.description || "").trim();
            if (!orig) continue;
            const lower = orig.toLowerCase();
            if (seen.has(lower)) continue; // keep first = most recent
            seen.add(lower);
            uniq.push(orig);
            uniqNorm.push(lower);
          }
          setSuggestions(uniq);
          setSuggestionsNorm(uniqNorm);
        } else {
          setSuggestions([]);
          setSuggestionsNorm([]);
        }

        const clientsResponse = await fetch(
          `${process.env.REACT_APP_API_URL}/clients`,
          {
            headers: { Authorization: `Bearer ${authToken}` },
          }
        );
        const clientsData = await clientsResponse.json();
        setClients(clientsData);

        const activitiesResponse = await fetch(
          `${process.env.REACT_APP_API_URL}/activities`,
          {
            headers: { Authorization: `Bearer ${authToken}` },
          }
        );
        const activitiesData = await activitiesResponse.json();
        setActivities(activitiesData);

        const typesResponse = await fetch(
          `${process.env.REACT_APP_API_URL}/types`,
          {
            headers: { Authorization: `Bearer ${authToken}` },
          }
        );
        const typesData = await typesResponse.json();
        setTypes(typesData);

        fetchRunningTimerData();
      } catch (error) {
        console.error("Error fetching data:", error);
      }
      setLoading(false);
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        fetchRunningTimerData();
      }
    };

    fetchData();
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [authToken]);

  const warnRowBeingEdited = (forForm) => {
    if (editing && forForm === null) {
      if (
        window.confirm(
          "Do you want to save changes to the currently edited row?"
        )
      ) {
        handleUpdate(editing);
      } else {
        setEditing(null);
      }
    }
  };

  const handleChange = (e, forForm = null) => {
    let value, name;
    if (e.target) {
      value = e.target.value;
      name = e.target.name;
    } else {
      value = e.value;
      name = e.name;
    }

    warnRowBeingEdited(forForm);

    if (forForm === null && !!formErrors[name]) {
      setFormErrors({ ...formErrors, [name]: "" });
    }

    if (name === "activity") {
      const activity = activityMap[value];
      if (forForm === null) {
        setForm({ ...form, activity: value, client: activity?.clientId || "" });
      } else if (forForm === "editForm") {
        setEditForm({
          ...editForm,
          activity: value,
          client: activity?.clientId || "",
        });
      }
    } else if (forForm === null) {
      setForm({ ...form, [name]: value });
    } else if (forForm === "editForm") {
      setEditForm({ ...editForm, [name]: value });
    }
  };

  const handleAutocomplete = (event, newValue) => {
    if (!newValue) {
      setForm({ ...form, description: "" });
    } else if (
      suggestionsNorm.includes((newValue || "").trim().toLowerCase())
    ) {
      const norm = (newValue || "").trim().toLowerCase();
      const selectedRecord = workHours.find(
        (wh) =>
          !wh.running && (wh.description || "").trim().toLowerCase() === norm
      );
      if (selectedRecord) {
        const { _id, date, hours, synced, ...rest } = selectedRecord;
        setForm({
          ...form,
          date: form.date,
          hours: DEFAULT_HOURS_IN_SECONDS,
          synced: false,
          ...rest,
        });
      }
    } else {
      setForm({ ...form, description: newValue });
    }
  };

  const onTimeFieldEditIsOtherTimeFieldLocked = (
    fieldBeingEdited,
    otherTimeField
  ) => {
    if (fieldBeingEdited === otherTimeField) return false;
    return onEditingTimeFieldsLockStates[fieldBeingEdited].isDefault
      ? onEditingTimeFieldsLockStates[fieldBeingEdited].default ===
          otherTimeField
      : onEditingTimeFieldsLockStates[fieldBeingEdited].other ===
          otherTimeField;
  };

  const handleStartTimeChange = (date, forForm = null) => {
    warnRowBeingEdited(forForm);
    if (forForm === null) {
      //if onEditingTimeFieldsLockStates["startTime"] currently is set to "endTime" then calculate the hours
      if (onTimeFieldEditIsOtherTimeFieldLocked("startTime", "endTime")) {
        const hours = form.hours - dayjs(date).diff(form.date, "seconds");
        setForm({ ...form, date: dayjs(date), hours });
      } else {
        //else nothing needs to be done on the hours since the end time gets calculated by default: startTime + hours
        setForm({ ...form, date: dayjs(date) });
      }
    } else if (forForm === "editForm") {
      setEditForm({ ...editForm, date: dayjs(date) });
    }
  };

  const handleHoursChange = (time, forForm = null) => {
    warnRowBeingEdited(forForm);
    let hours = time.diff(dayjs("1970-01-01T00:00:00"), "seconds");
    if (forForm === null) {
      if (onTimeFieldEditIsOtherTimeFieldLocked("hours", "endTime")) {
        const date = dayjs(form.date).subtract(hours - form.hours, "seconds");
        setForm({ ...form, date, hours });
      } else {
        //else nothing needs to be done on the hours since the end time gets calculated by default: startTime + hours
        setForm({ ...form, hours });
      }
    } else if (forForm === "editForm") {
      setEditForm({ ...editForm, hours });
    }
  };

  const handleEndTimeChange = (endDate, forForm = null) => {
    warnRowBeingEdited(forForm);
    if (forForm === null) {
      //if onEditingTimeFieldsLockStates["endTime"] currently is set to "hours" then calculate the start time
      if (onTimeFieldEditIsOtherTimeFieldLocked("endTime", "hours")) {
        const date = dayjs(endDate).subtract(form.hours, "seconds");
        setForm({ ...form, date });
      } else {
        //calculate the hours
        const hours = dayjs(endDate).diff(form.date, "seconds");
        setForm({ ...form, hours });
      }
    }
    //not in use in editForm
  };

  const [startTimeError, setStartTimeError] = React.useState(null);
  const startTimeErrorMessage = React.useMemo(() => {
    switch (startTimeError) {
      case "maxTime": {
        return "Start Time > End Time";
      }

      default: {
        return "";
      }
    }
  }, [startTimeError]);

  const [hourError, setHourError] = React.useState(null);
  const hourErrorMessage = React.useMemo(() => {
    switch (hourError) {
      case "minTime": {
        return "At least 1 min";
      }

      default: {
        return "";
      }
    }
  }, [hourError]);

  const [editHourError, setEditHourError] = React.useState(null);
  const editHourErrorMessage = React.useMemo(() => {
    switch (editHourError) {
      case "minTime": {
        return "At least 1 min";
      }

      default: {
        return "";
      }
    }
  }, [editHourError]);

  const [endTimeError, setEndTimeError] = React.useState(null);
  const endTimeErrorMessage = React.useMemo(() => {
    switch (endTimeError) {
      case "minTime": {
        return "End Time < Start Time";
      }

      default: {
        return "";
      }
    }
  }, [endTimeError]);

  const handleMainFormFocus = () => {
    if (editing) {
      if (
        window.confirm(
          "Do you want to save changes to the currently edited row?"
        )
      ) {
        handleUpdate(editing);
      } else {
        setEditing(null);
      }
    }
  };

  const firstRowFieldRef = useRef(null);
  const handleEdit = (workHour) => {
    setEditing(workHour._id);
    setEditForm({
      date: dayjs(workHour.date),
      hours: workHour.hours,
      description: workHour.description,
      client: workHour.client,
      activity: workHour.activity,
      type: workHour.type,
    });
    setTimeout(() => {
      if (firstRowFieldRef.current) {
        firstRowFieldRef.current.focus();
      }
    }, 0);
  };

  const handleSubmit = async () => {
    if (await isSessionValid(authToken, onLogout)) {
      if (form.hours < MIN_HOURS_IN_SECONDS) {
        return;
      }

      if (validateForm() === false) {
        return;
      }

      const { _id, ...formData } = form;

      setDescOpen(false); // Close Autocomplete so it doesn't pop open after submit

      fetch(`${process.env.REACT_APP_API_URL}/workhours`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify({ ...formData, date: formData.date.utc().unix() }),
      })
        .then((response) => response.json())
        .then((newWorkHour) => {
          const updatedWorkHour = {
            ...newWorkHour,
            date: dayjs.unix(newWorkHour.date),
          };
          const updatedWorkHours = [...workHours, updatedWorkHour].sort(
            (a, b) => b.date.valueOf() - a.date.valueOf()
          );
          setWorkHours(updatedWorkHours);

          const newOrig = form.description.trim();
          const newNorm = newOrig.toLowerCase();
          if (newOrig && !suggestionsNorm.includes(newNorm)) {
            setSuggestions([...suggestions, newOrig]);
            setSuggestionsNorm([...suggestionsNorm, newNorm]);
          }

          const endTime = dayjs(updatedWorkHour.date).add(
            updatedWorkHour.hours,
            "seconds"
          );

          setForm({ ...form, date: endTime });

          setDescOpen(false); // Extra guard: keep Autocomplete closed after add completes
        })
        .catch((error) => console.error("Error adding work hour:", error));
    }
  };

  const handleUpdate = async (id) => {
    if (await isSessionValid(authToken, onLogout)) {
      const errors = {};
      if (!editForm.activity) errors.activity = "Client/Activity is required";
      if (!editForm.type) errors.type = "Type is required";

      const selectedActivity = activityMap[editForm.activity];
      if (selectedActivity && selectedActivity.name === "TeamWork task") {
        const match = editForm.description.match(
          new RegExp(
            `${process.env.REACT_APP_APPRIX_TASK_NO_PREFIX}([0-9]{5,})`
          )
        );
        if (!match) {
          errors.description = "Must contain TW id";
          alert("Description must contain TW id like #12345678.");
        }
      }

      if (
        editForm.hours < MIN_HOURS_IN_SECONDS ||
        Object.keys(errors).length > 0
      ) {
        return;
      }

      const submissionEditForm = {
        ...editForm,
        date: dayjs(editForm.date).utc().unix(),
      };

      const oldWorkHour = workHours.find((wh) => wh._id === id);
      const oldDescription = oldWorkHour ? oldWorkHour.description : null;

      fetch(`${process.env.REACT_APP_API_URL}/workhours/${id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify(submissionEditForm),
      })
        .then((response) => response.json())
        .then((updatedWorkHour) => {
          setEditing(null);
          const convertedHandledWorkHour = {
            ...updatedWorkHour,
            date: dayjs.unix(updatedWorkHour.date),
          };
          const updatedWorkHours = workHours
            .map((wh) => (wh._id === id ? convertedHandledWorkHour : wh))
            .sort((a, b) => b.date.valueOf() - a.date.valueOf());
          setWorkHours(updatedWorkHours);

          if (oldDescription && oldDescription !== editForm.description) {
            const oldNorm = (oldDescription || "").trim().toLowerCase();
            const newOrig = (editForm.description || "").trim();
            const newNorm = newOrig.toLowerCase();

            const descriptionsInUseNo = updatedWorkHours.filter(
              (wh) => (wh.description || "").trim().toLowerCase() === oldNorm
            ).length;

            let nextSuggestions = suggestions;
            let nextNorm = suggestionsNorm;

            if (descriptionsInUseNo === 0) {
              nextSuggestions = nextSuggestions.filter(
                (d) => d.trim().toLowerCase() !== oldNorm
              );
              nextNorm = nextNorm.filter((d) => d !== oldNorm);
            }

            if (newOrig && !nextNorm.includes(newNorm)) {
              nextSuggestions = [...nextSuggestions, newOrig];
              nextNorm = [...nextNorm, newNorm];
            }

            if (nextSuggestions !== suggestions)
              setSuggestions(nextSuggestions);
            if (nextNorm !== suggestionsNorm) setSuggestionsNorm(nextNorm);
          }
        })
        .catch((error) => console.error("Error updating work hour:", error));
    }
  };

  const handleMainFormKeyDown = (e) => {
    if (!editing && e.key === "Enter") {
      if (!isPlaying) {
        handleSubmit();
      } else {
        handleStopClick();
      }
    }
  };

  const [onEditingTimeFieldsLockStates, setOnEditingTimesFieldLockStates] =
    useState({
      startTime: { default: "hours", other: "endTime", isDefault: true },
      hours: { default: "startTime", other: "endTime", isDefault: true },
      endTime: { default: "startTime", other: "hours", isDefault: true },
    });

  const lockedField = React.useMemo(() => {
    if (!editingFocusField) return null;
    const s = onEditingTimeFieldsLockStates[editingFocusField];
    return s.isDefault ? s.default : s.other;
  }, [editingFocusField, onEditingTimeFieldsLockStates]);

  const handleTimeFieldsLockChangesOnKeydown = (e, fieldBeingEdited) => {
    if (e.key === " ") {
      e.preventDefault();

      //toggle the lock state of the field being edited
      setOnEditingTimesFieldLockStates((prev) => ({
        ...prev,
        [fieldBeingEdited]: {
          ...prev[fieldBeingEdited],
          isDefault: !prev[fieldBeingEdited].isDefault,
        },
      }));
    }
  };

  const handleEditFormKeyDown = (e) => {
    if (e.key === "Enter") {
      handleUpdate(editing);
    } else if (e.key === "Escape") {
      setEditing(null);
    }
  };

  const handleDelete = async (id) => {
    if (await isSessionValid(authToken, onLogout)) {
      if (!window.confirm("Are you sure you want to delete this entry?")) {
        return;
      }
      const del = workHours.find((wh) => wh._id === id);
      fetch(`${process.env.REACT_APP_API_URL}/workhours/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${authToken}` },
      })
        .then(() => {
          const updated = workHours.filter((wh) => wh._id !== id);
          setWorkHours(updated);
          if (del) {
            const delNorm = (del.description || "").trim().toLowerCase();
            const stillUsed = updated.some(
              (wh) => (wh.description || "").trim().toLowerCase() === delNorm
            );
            if (!stillUsed && delNorm) {
              setSuggestions(
                suggestions.filter((d) => d.trim().toLowerCase() !== delNorm)
              );
              setSuggestionsNorm(suggestionsNorm.filter((d) => d !== delNorm));
            }
          }
        })
        .catch((error) => console.error("Error deleting work hour:", error));
    }
  };

  const displayTimePeriod = (start, hours) => {
    const end = dayjs(start).add(hours, "seconds");
    return `${dayjs(start).format("HH:mm")} - ${end.format("HH:mm")}`;
  };

  const displayHours = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.round((seconds % 3600) / 60);
    const paddedHours = hours.toString().padStart(2, "0");
    const paddedMinutes = minutes.toString().padStart(2, "0");
    return `${paddedHours}:${paddedMinutes}`;
  };

  const getStartOfWeek = (date) => {
    //dayjs's start of the week day is Sun
    //getting the right Mon could also be done smarter using dayjs/plugin/isoWeek
    //but that would require a plugin and this is simple enough
    const day = date.day();
    const diff = (day === 0 ? -6 : 1) - day;
    return date.add(diff, "day").startOf("day");
  };

  const { totalToday, totalWeek, totalMonth } = React.useMemo(() => {
    const today = dayjs().startOf("day");
    const startOfWeek = getStartOfWeek(dayjs());
    const startOfMonth = dayjs().startOf("month");
    let secToday = 0,
      secWeek = 0,
      secMonth = 0;
    for (const wh of workHours) {
      const d = wh.date;
      const secs = wh.hours || 0;
      if (d.isSame(today, "day")) secToday += secs;
      if (d.isAfter(startOfWeek)) secWeek += secs;
      if (d.isAfter(startOfMonth)) secMonth += secs;
    }
    return {
      totalToday: displayHours(secToday),
      totalWeek: displayHours(secWeek),
      totalMonth: displayHours(secMonth),
    };
  }, [workHours]);

  const groupWorkHoursByDate = (workHours) => {
    const groups = workHours.reduce((groups, workHour) => {
      const dateObj = workHour.date;
      const today = dayjs().startOf("day");
      const yesterday = dayjs().subtract(1, "day").startOf("day");
      const dateFormat = "D.M.";
      const dateFormatWithYear = "D.M.YYYY";

      let date;
      if (dateObj.isSame(today, "day")) {
        date = `Today, ${dateObj.format(dateFormat)}`;
      } else if (dateObj.isSame(yesterday, "day")) {
        date = `Yesterday, ${dateObj.format(dateFormat)}`;
      } else {
        //check if date older than 1 year
        const yearDiff = dayjs().diff(dateObj, "year");
        if (yearDiff < 1) {
          date = dateObj.format("dddd, " + dateFormat);
        } else {
          date = dateObj.format("dddd, " + dateFormatWithYear);
        }
      }

      if (!groups[date]) {
        groups[date] = { totalHours: 0, workHours: [] };
      }
      groups[date].totalHours += workHour.hours;
      groups[date].workHours.push(workHour);
      return groups;
    }, {});

    return Object.entries(groups).map(([date, group]) => ({
      date,
      totalHours: group.totalHours,
      workHours: group.workHours,
    }));
  };

  const groupedWorkHours = React.useMemo(
    () => groupWorkHoursByDate(workHours),
    [workHours]
  );

  const validateForm = useCallback(() => {
    const errors = {};
    if (!form.activity) errors.activity = "Client/Activity is required";
    if (!form.type) errors.type = "Type is required";

    /*const forbiddenCharsRegex = /[äåöÄÅÖ]/;
    if (forbiddenCharsRegex.test(form.description)) {
      errors.description = "No chars like 'ä', 'å', 'ö'.";
    }*/

    const selectedActivity = activityMap[form.activity];
    if (selectedActivity && selectedActivity.name === "TeamWork task") {
      const match = form.description.match(
        new RegExp(`${process.env.REACT_APP_APPRIX_TASK_NO_PREFIX}([0-9]{5,})`)
      );
      if (!match) {
        errors.description = "Must contain TW id";
      }
    }

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return false;
    }

    setFormErrors({});
    return true;
  }, [form, activityMap]);

  const handlePlayClick = useCallback(async () => {
    if (await isSessionValid(authToken, onLogout)) {
      setDescOpen(false);
      if (validateForm() === false) {
        return;
      }

      const hoursToUse = 0;
      const dateToUse = dayjs();

      setForm({ ...form, date: dateToUse, hours: hoursToUse });

      setIsPlaying(true);
      setRunningTimerStart(dateToUse.unix());

      fetch(`${process.env.REACT_APP_API_URL}/workhours/start`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify({
          ...form,
          date: dateToUse.unix(),
          hours: hoursToUse,
        }),
      })
        .then((response) => response.json())
        .then((newWorkHour) => {
          setRunningTimerId(newWorkHour._id);
        })
        .catch((error) => console.error("Error starting timer:", error));
    }
  }, [authToken, form, onLogout, validateForm]);

  const RunningTimerDisplay = React.forwardRef(
    ({ isPlaying, startUnix }, ref) => {
      const [seconds, setSeconds] = useState(0);

      // Recalculate base when start time changes
      useEffect(() => {
        if (startUnix != null) setSeconds(dayjs().unix() - startUnix);
        else setSeconds(0);
      }, [startUnix]);

      // Tick without re-rendering the parent
      useEffect(() => {
        if (!isPlaying) return;
        const id = setInterval(() => setSeconds((s) => s + 1), 1000);
        return () => clearInterval(id);
      }, [isPlaying, startUnix]);

      useImperativeHandle(ref, () => ({ getSeconds: () => seconds }), [
        seconds,
      ]);

      const h = String(Math.floor(seconds / 3600)).padStart(2, "0");
      const m = String(Math.floor((seconds % 3600) / 60)).padStart(2, "0");
      const s = String(seconds % 60).padStart(2, "0");
      return (
        <>
          {h}:{m}:{s}
        </>
      );
    }
  );

  const handleStopClick = async () => {
    if (await isSessionValid(authToken, onLogout)) {
      setDescOpen(false); // keep Autocomplete closed during/after stop
      setIsPlaying(false);
      const seconds = timerDisplayRef.current?.getSeconds?.() ?? 0;
      const roundedRunningTime = Math.round(seconds / 60) * 60;
      setRunningTimerStart(null);

      setForm({ ...form, hours: roundedRunningTime });

      if (roundedRunningTime >= MIN_HOURS_IN_SECONDS) {
        fetch(
          `${process.env.REACT_APP_API_URL}/workhours/stop/${runningTimerId}`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${authToken}`,
            },
            body: JSON.stringify({
              hours: roundedRunningTime,
              description: form.description,
              client: form.client,
              activity: form.activity,
              type: form.type,
            }),
          }
        )
          .then((response) => response.json())
          .then((updatedWorkHour) => {
            setFormErrors({});
            setHourError(null);
            setForm({ ...form, hours: DEFAULT_HOURS_IN_SECONDS });
            const convertedUpdatedWorkHour = {
              ...updatedWorkHour,
              date: dayjs.unix(updatedWorkHour.date),
            };
            const updatedWorkHours = [
              ...workHours,
              convertedUpdatedWorkHour,
            ].sort((a, b) => b.date.valueOf() - a.date.valueOf());
            setWorkHours(updatedWorkHours);
            const newOrig = (form.description || "").trim();
            const newNorm = newOrig.toLowerCase();
            if (newOrig && !suggestionsNorm.includes(newNorm)) {
              setSuggestions([...suggestions, newOrig]);
              setSuggestionsNorm([...suggestionsNorm, newNorm]);
            }
            setDescOpen(false); // ensure popup stays closed after stop completes
          })
          .catch((error) => console.error("Error stopping timer:", error));
      } else {
        setForm({ ...form, hours: 0 });
        delete form._id;
        setDescOpen(false); // also close when stopping without saving
        fetch(`${process.env.REACT_APP_API_URL}/workhours/${runningTimerId}`, {
          method: "DELETE",
          headers: { Authorization: `Bearer ${authToken}` },
        }).catch((error) => console.error("Error deleting work hour:", error));
      }
    }
  };

  const [shouldPlay, setShouldPlay] = useState(false);
  useEffect(() => {
    if (shouldPlay) {
      handlePlayClick();
      setShouldPlay(false);
    }
  }, [shouldPlay, form, handlePlayClick]);

  const handlePlayFromRow = (workHour) => {
    const { description, activity, client, type } = workHour;

    const obj = {
      ...form,
      description,
      activity,
      client,
      type,
      date: dayjs(),
    };
    setShouldPlay(true);
    setForm(obj);
  };

  //Create the worker once (mounted), and clean up
  useEffect(() => {
    if (!descWorkerRef.current) {
      descWorkerRef.current = new Worker(
        new URL("./descIndex.worker.js", import.meta.url),
        { type: "module" }
      );
    }
    const w = descWorkerRef.current;
    const onMessage = (e) => {
      const { type, result, qid } = e.data || {};
      if (type === "built") {
        setIndexReady(true);
        setDescLoading(false); // ensure spinner resets after rebuild
        return;
      }
      if (type === "result" && qid === lastQueryIdRef.current) {
        const idxs = result || [];
        const out = [];
        for (let i = 0; i < idxs.length && out.length < AUTOCOMPLETE_MAX; i++) {
          out.push(suggestionsRef.current[idxs[i]]);
        }
        setDerivedDescOptions(out);
        setDescLoading(false);
      }
    };
    w.addEventListener("message", onMessage);
    return () => {
      w.removeEventListener("message", onMessage);
      w.terminate();
      descWorkerRef.current = null;
    };
  }, []);

  //(Re)build the trigram index in the worker when suggestions change
  useEffect(() => {
    if (!descWorkerRef.current) return;
    setIndexReady(false);
    descWorkerRef.current.postMessage({ type: "build", suggestionsNorm });
  }, [suggestionsNorm]);

  //Drive queries from the input (substring match, with early exit)
  useEffect(() => {
    const q = (descQuery || "").trim().toLowerCase();
    // default suggestions when empty
    if (!q) {
      lastQueryIdRef.current++; // invalidate any in-flight worker results
      setDerivedDescOptions(topSuggestions);
      setDescLoading(false);
      return;
    }
    // For very short queries, do a tiny capped linear scan on the main thread
    if (q.length < TRIGRAM_LEN || !descWorkerRef.current || !indexReady) {
      lastQueryIdRef.current++; // prevent stale worker responses from applying
      const out = [];
      for (
        let i = 0;
        i < suggestionsNorm.length && out.length < AUTOCOMPLETE_MAX;
        i++
      ) {
        if (suggestionsNorm[i].includes(q)) out.push(suggestionsRef.current[i]);
      }
      setDerivedDescOptions(out);
      setDescLoading(false);
      return;
    }
    // Worker-based trigram query with early exit
    // Clear old suggestions so the popup won't briefly show stale matches
    setDerivedDescOptions([]);
    setDescLoading(true);
    const qid = ++lastQueryIdRef.current; // current query version
    descWorkerRef.current.postMessage({
      type: "query",
      q,
      limit: AUTOCOMPLETE_MAX,
      qid,
    });
  }, [descQuery, topSuggestions, suggestionsNorm, indexReady]);

  //Keep descInput in sync when form value changes externally (optional but nice)
  useEffect(() => {
    setDescInput(form.description || "");
  }, [form.description]);

  const typeMap = React.useMemo(() => {
    const m = {};
    for (const t of types) m[t._id] = t;
    return m;
  }, [types]);

  return (
    <React.Fragment>
      {!loading && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            m: 2,
          }}
        >
          <img src={loop4Img} alt="Loop Logo" style={{ height: "50px" }} />
          <Box sx={{ textAlign: "center", flexGrow: 1 }}>
            <Box sx={{ color: "primary.main" }}>
              Today: {totalToday} <span style={{ whiteSpace: "pre" }}> </span>|
              <span style={{ whiteSpace: "pre" }}> </span> Week: {totalWeek}{" "}
              <span style={{ whiteSpace: "pre" }}> </span>|
              <span style={{ whiteSpace: "pre" }}> </span> Month: {totalMonth}
            </Box>
          </Box>
          <Link
            href="#"
            onClick={(e) => {
              e.preventDefault();
              onViewChange();
            }}
            underline="none"
            color={"secondary"}
            sx={{ cursor: "pointer", marginRight: 2 }}
          >
            filter
          </Link>
          <Link
            href="#"
            onClick={(e) => {
              e.preventDefault();
              onLogout();
            }}
            underline="none"
            color={"secondary"}
            sx={{ cursor: "pointer", marginLeft: "auto" }}
          >
            logout
          </Link>
        </Box>
      )}
      {loading && (
        <Paper
          sx={{
            minHeight: 56,
          }}
        />
      )}
      {!loading && (
        <Paper
          sx={{
            m: 2,
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(60px, 1fr))",
            gap: 2,
            gridAutoFlow: "row dense",
          }}
        >
          <Autocomplete
            freeSolo
            options={derivedDescOptions}
            filterOptions={(x) => x} // we already filtered & ordered (recency)
            loading={descLoading}
            open={descOpen && !typing && derivedDescOptions.length > 0}
            onOpen={() => setDescOpen(true)}
            onClose={() => setDescOpen(false)}
            value={form.description}
            inputValue={descInput}
            onInputChange={(event, newValue) => {
              setDescInput(newValue);
              if (!newValue || newValue.trim() === "")
                setDescOpen(false); // close when cleared fast
              else setDescOpen(true); // allow opening once debounce settles
              handleChange({
                target: { name: "description", value: newValue },
              });
            }}
            onChange={(event, newValue) => {
              setDescInput(newValue || "");
              setDescOpen(false); // close popup after selection
              handleAutocomplete(event, newValue);
            }}
            sx={{ gridColumn: "span 4", minWidth: 100 }}
            renderInput={(params) => (
              <Box>
                <TextField
                  {...params}
                  label="Description"
                  name="description"
                  onFocus={handleMainFormFocus}
                  onKeyDown={handleMainFormKeyDown}
                  error={!!formErrors.description}
                />
                {formErrors.description && (
                  <Box sx={{ color: "red", mt: 1 }}>
                    {formErrors.description}
                  </Box>
                )}
              </Box>
            )}
          />
          <FormControl
            sx={{ gridColumn: "span 2" }}
            error={!!formErrors.activity}
          >
            <InputLabel id="client-activity-label">Client/Activity</InputLabel>
            <Select
              labelId="client-activity-label"
              name="activity"
              value={form.activity}
              onFocus={handleMainFormFocus}
              onChange={(e) => handleChange(e, null)}
              onKeyDown={handleMainFormKeyDown}
              label="Client/Activity"
            >
              {clientActivityMenuItems}
            </Select>
            {formErrors.activity && (
              <Box sx={{ color: "red", mt: 1 }}>{formErrors.activity}</Box>
            )}
          </FormControl>
          <FormControl
            sx={{ gridColumn: "span 2", minWidth: 100 }}
            error={!!formErrors.type}
          >
            <InputLabel id="type-label">Type</InputLabel>
            <Select
              labelId="type-label"
              name="type"
              value={form.type}
              onFocus={handleMainFormFocus}
              onChange={(e) => handleChange(e, null)}
              onKeyDown={handleMainFormKeyDown}
              label="Type"
            >
              {types.map((type) => (
                <MenuItem key={type._id} value={type._id}>
                  {type.name}
                </MenuItem>
              ))}
            </Select>
            {formErrors.type && (
              <Box sx={{ color: "red", mt: 1 }}>{formErrors.type}</Box>
            )}
          </FormControl>
          {!isPlaying ? (
            <>
              <Box
                sx={{ gridColumn: "span 2", position: "relative" }}
                onFocus={() => {
                  handleMainFormFocus();
                  setEditingFocusField("startTime");
                }}
                onBlur={() => setEditingFocusField(null)}
                onKeyDown={(e) => {
                  handleMainFormKeyDown(e);
                  handleTimeFieldsLockChangesOnKeydown(e, "startTime");
                }}
              >
                <Box
                  sx={{
                    position: "absolute",
                    top: "-10px",
                    right: "-9px",
                    zIndex: 10,
                    visibility:
                      editingFocusField === "startTime" ? "visible" : "hidden",
                  }}
                >
                  <Tooltip
                    title="When updating the Start Time, either the End Time or the Hours will automatically adjust. Press the Spacebar to select which of the field shall remain unchanged during the update."
                    placement="left-end"
                  >
                    <Box
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor: "#fff",
                        borderRadius: "50%",
                        width: 20,
                        height: 20,
                      }}
                    >
                      <InfoIcon sx={{ color: grey[500] }} />
                    </Box>
                  </Tooltip>
                </Box>
                <Box
                  sx={{
                    position: "absolute",
                    top: "-12px",
                    right: "-8px",
                    zIndex: 10,
                    visibility:
                      lockedField === "startTime" && editingFocusField
                        ? "visible"
                        : "hidden",
                  }}
                >
                  <Tooltip
                    title="This field is locked and won't be adjusted on updates. Press the Spacebar to unlock."
                    placement="left-end"
                  >
                    <LockIcon color="warning" />
                  </Tooltip>
                </Box>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DateTimePicker
                    label="Start Time"
                    value={form.date}
                    onChange={(date) => handleStartTimeChange(date)}
                    onOpen={() => {
                      handleMainFormFocus();
                      setEditingFocusField("startTime");
                    }}
                    onClose={() => {
                      setTimeout(() => setEditingFocusField(null), 10);
                    }}
                    format="D.M. H:mm"
                    maxDateTime={
                      editingFocusField === "startTime" &&
                      onTimeFieldEditIsOtherTimeFieldLocked(
                        "startTime",
                        "endTime"
                      )
                        ? dayjs(form.date)
                            .add(form.hours, "seconds")
                            .subtract(MIN_HOURS_IN_SECONDS, "seconds")
                        : null
                    }
                    onError={(newError) => setStartTimeError(newError)}
                    slotProps={{
                      textField: {
                        helperText: startTimeErrorMessage,
                      },
                      field: { shouldRespectLeadingZeros: true },
                    }}
                    ampm={false}
                    viewRenderers={{
                      hours: renderTimeViewClock,
                      minutes: renderTimeViewClock,
                      seconds: renderTimeViewClock,
                    }}
                  />
                </LocalizationProvider>
              </Box>
              <Box
                sx={{ gridColumn: "span 2", position: "relative" }}
                onFocus={() => {
                  handleMainFormFocus();
                  setEditingFocusField("hours");
                }}
                onBlur={() => setEditingFocusField(null)}
                onKeyDown={(e) => {
                  handleMainFormKeyDown(e);
                  handleTimeFieldsLockChangesOnKeydown(e, "hours");
                }}
              >
                <Box
                  sx={{
                    position: "absolute",
                    top: "-10px",
                    right: "-10px",
                    zIndex: 10,
                    visibility:
                      editingFocusField === "hours" ? "visible" : "hidden",
                  }}
                >
                  <Tooltip
                    title="When updating Hours, either the Start Time or the End Time will automatically adjust. Press the Spacebar to select which of the fields shall remain unchanged during the update."
                    placement="left-end"
                  >
                    <Box
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor: "#fff",
                        borderRadius: "50%",
                        width: 20,
                        height: 20,
                      }}
                    >
                      <InfoIcon sx={{ color: grey[500] }} />
                    </Box>
                  </Tooltip>
                </Box>
                <Box
                  sx={{
                    position: "absolute",
                    top: "-12px",
                    right: "-8px",
                    zIndex: 10,
                    visibility:
                      lockedField === "hours" && editingFocusField
                        ? "visible"
                        : "hidden",
                  }}
                >
                  <Tooltip
                    title="This field is locked and won't be adjusted on updates. Press the Spacebar to unlock."
                    placement="left-end"
                  >
                    <LockIcon color="warning" />
                  </Tooltip>
                </Box>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <TimePicker
                    label="Hours"
                    value={dayjs("1970-01-01T00:00:00").add(
                      form.hours,
                      "seconds"
                    )}
                    onChange={(time) => handleHoursChange(time)}
                    onOpen={() => {
                      handleMainFormFocus();
                      setEditingFocusField("hours");
                    }}
                    onClose={() => {
                      setTimeout(() => setEditingFocusField(null), 10);
                    }}
                    format="H:mm"
                    minTime={dayjs("1970-01-01T00:01:00")}
                    onError={(newError) => setHourError(newError)}
                    slotProps={{
                      textField: {
                        helperText: hourErrorMessage,
                      },
                      field: { shouldRespectLeadingZeros: true },
                    }}
                    ampm={false}
                  />
                </LocalizationProvider>
              </Box>
              <Box
                sx={{ gridColumn: "span 2", position: "relative" }}
                onFocus={() => {
                  handleMainFormFocus();
                  setEditingFocusField("endTime");
                }}
                onBlur={() => setEditingFocusField(null)}
                onKeyDown={(e) => {
                  handleMainFormKeyDown(e);
                  handleTimeFieldsLockChangesOnKeydown(e, "endTime");
                }}
              >
                <Box
                  sx={{
                    position: "absolute",
                    top: "-10px",
                    right: "-10px",
                    zIndex: 10,
                    visibility:
                      editingFocusField === "endTime" ? "visible" : "hidden",
                  }}
                >
                  <Tooltip
                    title="When updating End Time, either the Start Time or the Hours will automatically adjust. Press the Spacebar to select which of the fields shall remain unchanged during the update."
                    placement="left-end"
                  >
                    <Box
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor: "#fff",
                        borderRadius: "50%",
                        width: 20,
                        height: 20,
                      }}
                    >
                      <InfoIcon sx={{ color: grey[500] }} />
                    </Box>
                  </Tooltip>
                </Box>
                <Box
                  sx={{
                    position: "absolute",
                    top: "-12px",
                    right: "-8px",
                    zIndex: 10,
                    visibility:
                      lockedField === "endTime" && editingFocusField
                        ? "visible"
                        : "hidden",
                  }}
                >
                  <Tooltip
                    title="This field is locked and won't be adjusted on updates. Press the Spacebar to unlock."
                    placement="left-end"
                  >
                    <LockIcon color="warning" />
                  </Tooltip>
                </Box>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DateTimePicker
                    label="End Time"
                    value={dayjs(form.date).add(form.hours, "seconds")}
                    onChange={(date) => handleEndTimeChange(date)}
                    onOpen={() => {
                      handleMainFormFocus();
                      setEditingFocusField("endTime");
                    }}
                    onClose={() => {
                      setTimeout(() => setEditingFocusField(null), 10);
                    }}
                    format="D.M. H:mm"
                    minDateTime={
                      editingFocusField === "endTime" &&
                      onTimeFieldEditIsOtherTimeFieldLocked(
                        "endTime",
                        "startTime"
                      )
                        ? dayjs(form.date).add(MIN_HOURS_IN_SECONDS, "seconds")
                        : null
                    }
                    onError={(newError) => setEndTimeError(newError)}
                    slotProps={{
                      textField: {
                        helperText: endTimeErrorMessage,
                      },
                      field: { shouldRespectLeadingZeros: true },
                    }}
                    ampm={false}
                    viewRenderers={{
                      hours: renderTimeViewClock,
                      minutes: renderTimeViewClock,
                      seconds: renderTimeViewClock,
                    }}
                  />
                </LocalizationProvider>
              </Box>
              <Button
                onFocus={handleMainFormFocus}
                onClick={handleSubmit}
                color="primary"
                variant="contained"
                sx={{
                  gridColumn: "span 1",
                  minWidth: 56,
                  minHeight: 56,
                  maxHeight: 56,
                }}
              >
                <AddIcon />
              </Button>
            </>
          ) : (
            <>
              <Box sx={{ gridColumn: "span 3", minWidth: 195 }} />
              <Box
                sx={{
                  gridColumn: "span 2",
                  minWidth: 110,
                  lineHeight: "56px",
                  textAlign: "center",
                  fontSize: "0.9rem",
                  marginLeft: -1.5,
                }}
              >
                <RunningTimerDisplay
                  ref={timerDisplayRef}
                  isPlaying={isPlaying}
                  startUnix={runningTimerStart}
                />
              </Box>
              <Box sx={{ gridColumn: "span 1", minWidth: 56 }} />
            </>
          )}
          {!isPlaying ? (
            <Button
              onFocus={handleMainFormFocus}
              onClick={handlePlayClick}
              color="primary"
              variant="contained"
              sx={{
                gridColumn: "span 1",
                minWidth: 56,
                minHeight: 56,
                maxHeight: 56,
              }}
            >
              <PlayArrowIcon />
            </Button>
          ) : (
            <Button
              onFocus={handleMainFormFocus}
              onClick={handleStopClick}
              color="primary"
              variant="contained"
              sx={{
                gridColumn: "span 1",
                minWidth: 56,
                minHeight: 56,
                maxHeight: 56,
              }}
            >
              <StopIcon />
            </Button>
          )}
        </Paper>
      )}

      <Box sx={{ m: 2 }}>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <StyledTableHeaderCell span={4}>
                  {/* Description */}
                </StyledTableHeaderCell>
                <StyledTableHeaderCell span={2}>
                  {/* Client/Activity */}
                </StyledTableHeaderCell>
                <StyledTableHeaderCell span={2}>
                  {/* Type */}
                </StyledTableHeaderCell>
                <StyledTableHeaderCell span={2}>
                  {/* Date */}
                </StyledTableHeaderCell>
                <StyledTableHeaderCell span={2}>
                  {/* Hours */}
                </StyledTableHeaderCell>
                <StyledTableHeaderCell span={2}>
                  {/* Empty */}
                </StyledTableHeaderCell>
                <StyledTableHeaderCell span={2} sx={{ textAlign: "center" }}>
                  {/* Actions */}
                </StyledTableHeaderCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {groupedWorkHours.map(({ date, totalHours, workHours }) => (
                <React.Fragment key={date}>
                  <TableRow>
                    <TableCell
                      colSpan={4}
                      sx={{
                        fontSize: "1rem",
                        paddingLeft: 1,
                        paddingTop: 5,
                      }}
                    >
                      {date}
                    </TableCell>
                    <TableCell
                      sx={{
                        paddingTop: 5,
                        textAlign: "center",
                      }}
                    >
                      {displayHours(totalHours)}
                    </TableCell>
                    <TableCell colSpan={2} />
                  </TableRow>
                  {workHours.map((workHour) => (
                    <TableRow
                      key={workHour._id}
                      sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                    >
                      <TableCell
                        sx={{
                          paddingTop: editing === workHour._id ? "default" : 0,
                          paddingBottom:
                            editing === workHour._id ? "default" : 0,
                          paddingLeft: 1,
                          paddingRight: 1,
                        }}
                      >
                        {editing === workHour._id ? (
                          <TextField
                            name="description"
                            label="Description"
                            value={editForm.description}
                            onChange={(e) => handleChange(e, "editForm")}
                            onKeyDown={handleEditFormKeyDown}
                            inputRef={firstRowFieldRef}
                            fullWidth
                          />
                        ) : (
                          workHour.description
                        )}
                      </TableCell>
                      <TableCell
                        sx={{
                          paddingTop: editing === workHour._id ? "default" : 0,
                          paddingBottom:
                            editing === workHour._id ? "default" : 0,
                          paddingLeft: 1,
                          paddingRight: 1,
                        }}
                      >
                        {editing === workHour._id ? (
                          <FormControl fullWidth>
                            <InputLabel id="edit-client-activity-label">
                              Client/Activity
                            </InputLabel>
                            <Select
                              labelId="edit-client-activity-label"
                              name="activity"
                              value={editForm.activity}
                              onChange={(e) => handleChange(e, "editForm")}
                              onKeyDown={handleEditFormKeyDown}
                              label="Client/Activity"
                            >
                              {clientActivityMenuItems}
                            </Select>
                          </FormControl>
                        ) : (
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "center",
                            }}
                          >
                            {getClientActivityDisplay(workHour)}
                          </Box>
                        )}
                      </TableCell>
                      <TableCell
                        sx={{
                          paddingTop: editing === workHour._id ? "default" : 0,
                          paddingBottom:
                            editing === workHour._id ? "default" : 0,
                          paddingLeft: 1,
                          paddingRight: 1,
                        }}
                      >
                        {editing === workHour._id ? (
                          <FormControl fullWidth>
                            <InputLabel id="edit-type-label">Type</InputLabel>
                            <Select
                              labelId="edit-type-label"
                              name="type"
                              value={editForm.type}
                              onChange={(e) => handleChange(e, "editForm")}
                              onKeyDown={handleEditFormKeyDown}
                              label="Type"
                            >
                              {types.map((type) => (
                                <MenuItem key={type._id} value={type._id}>
                                  {type.name}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        ) : (
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "center",
                            }}
                          >
                            {typeMap[workHour.type]?.name || ""}
                          </Box>
                        )}
                      </TableCell>
                      <TableCell
                        sx={{
                          paddingTop: editing === workHour._id ? "default" : 0,
                          paddingBottom:
                            editing === workHour._id ? "default" : 0,
                          paddingLeft: 1,
                          paddingRight: 1,
                        }}
                      >
                        {editing === workHour._id ? (
                          <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DateTimePicker
                              label="Start Time"
                              value={editForm.date}
                              onChange={(date) =>
                                handleStartTimeChange(date, "editForm")
                              }
                              textField={(params) => (
                                <TextField
                                  {...params}
                                  onKeyDown={handleEditFormKeyDown}
                                />
                              )}
                              ampm={false}
                              format="D.M. HH:mm"
                              slotProps={{
                                field: { shouldRespectLeadingZeros: true },
                              }}
                              viewRenderers={{
                                hours: renderTimeViewClock,
                                minutes: renderTimeViewClock,
                                seconds: renderTimeViewClock,
                              }}
                            />
                          </LocalizationProvider>
                        ) : (
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "center",
                            }}
                          >
                            {displayTimePeriod(workHour.date, workHour.hours)}
                          </Box>
                        )}
                      </TableCell>
                      <TableCell
                        sx={{
                          paddingTop: editing === workHour._id ? "default" : 0,
                          paddingBottom:
                            editing === workHour._id ? "default" : 0,
                          paddingLeft: 1,
                          paddingRight: 1,
                        }}
                      >
                        {editing === workHour._id ? (
                          <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <TimePicker
                              label="Hours"
                              value={dayjs("1970-01-01T00:00:00").add(
                                editForm.hours,
                                "seconds"
                              )}
                              onChange={(time) =>
                                handleHoursChange(time, "editForm")
                              }
                              onKeyDown={handleEditFormKeyDown}
                              minTime={dayjs("1970-01-01T00:01:00")}
                              onError={(newError) => setEditHourError(newError)}
                              slotProps={{
                                textField: {
                                  helperText: editHourErrorMessage,
                                },
                              }}
                              ampm={false}
                            />
                          </LocalizationProvider>
                        ) : (
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "center",
                            }}
                          >
                            {displayHours(workHour.hours)}
                          </Box>
                        )}
                      </TableCell>
                      <TableCell></TableCell>
                      <TableCell
                        sx={{
                          textAlign: "center",
                          paddingTop: editing === workHour._id ? "default" : 0,
                          paddingBottom:
                            editing === workHour._id ? "default" : 0,
                          paddingLeft: 1,
                          paddingRight: 1,
                        }}
                      >
                        {editing === workHour._id ? (
                          <StyledIconButton
                            onClick={() => handleUpdate(workHour._id)}
                            color="primary"
                          >
                            <SaveIcon />
                          </StyledIconButton>
                        ) : (
                          <>
                            <StyledIconButton
                              color="secondary"
                              onClick={() => handleEdit(workHour)}
                            >
                              <EditIcon />
                            </StyledIconButton>
                            <StyledIconButton
                              color="secondary"
                              onClick={() => handleDelete(workHour._id)}
                            >
                              <DeleteIcon />
                            </StyledIconButton>
                            <StyledIconButton
                              color="secondary"
                              onClick={() => handlePlayFromRow(workHour)}
                            >
                              <PlayArrowIcon />
                            </StyledIconButton>
                          </>
                        )}
                        {editing === workHour._id && (
                          <StyledIconButton
                            onClick={() => setEditing(null)}
                            color="secondary"
                          >
                            <CloseIcon />
                          </StyledIconButton>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        {!loading && showCountsBox && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              m: 1,
              color: "text.secondary",
              textAlign: "center",
            }}
          >
            <span>
              {workHours.length} / {serverTotalCount ?? workHours.length}{" "}
              records displayed
              {serverTotalCount != null &&
                workHours.length < serverTotalCount && (
                  <>
                    {" (you can view more on the "}
                    <Link
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        onViewChange();
                      }}
                      underline="none"
                      color={"secondary"}
                      sx={{ cursor: "pointer" }}
                    >
                      filter
                    </Link>
                    {" page)"}
                  </>
                )}
            </span>
          </Box>
        )}
      </Box>
    </React.Fragment>
  );
};

export default WorkHoursTable;
